import { type ReactNode, createContext, useContext, useEffect, useState } from "react"
import { applyTenantTheme, fetchTenants, resolveTenant } from "../lib/tenant"
import type { TenantConfig } from "../types/tenant.types"

interface TenantContextValue {
  tenant: TenantConfig | null
  isLoading: boolean
  error: string | null
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  isLoading: true,
  error: null,
})

interface TenantProviderProps {
  children: ReactNode
  tenantsPath?: string
  onTenantLoaded?: (tenant: TenantConfig) => void
}

export function TenantProvider({
  children,
  tenantsPath = "/tenants.json",
  onTenantLoaded,
}: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchTenants(tenantsPath)
      .then((tenants) => {
        const resolved = resolveTenant(tenants)
        if (resolved) {
          applyTenantTheme(resolved)
          setTenant(resolved)
          onTenantLoaded?.(resolved)
        } else {
          setError("No matching tenant found")
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load tenants")
      })
      .finally(() => setIsLoading(false))
  }, [tenantsPath, onTenantLoaded])

  return (
    <TenantContext.Provider value={{ tenant, isLoading, error }}>{children}</TenantContext.Provider>
  )
}

export function useTenant(): TenantContextValue {
  return useContext(TenantContext)
}
